import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Space, Spin, Tag, Empty, message } from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  CalendarOutlined,
  RedoOutlined,
  SendOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { GET_REVENUE_INVOICE_BY_BILL_ID } from '../../../graphql/queries/settlement/revenueInvoiceQueries';
import type { RevenueInvoiceType } from '../../../graphql/types/settlement/revenueInvoice';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import { openBillExportFile } from '../../../utils/billExportDownload';
import { formatCentsAmount, formatYyyymmdd } from '../../../utils/invoiceAmount';
import InvoiceDueDateModal from '../../../components/settlement/InvoiceDueDateModal';
import InvoiceReissueModal from '../../../components/settlement/InvoiceReissueModal';
import InvoiceSendModal from '../../../components/settlement/InvoiceSendModal';

const STATUS_TAG: Record<string, { color: string; text: string }> = {
  INITIATED: { color: 'processing', text: 'PDF 生成中' },
  SUCCESS: { color: 'green', text: '生效中' },
  FAILED: { color: 'red', text: 'PDF 生成失败' },
  CANCELLED: { color: 'default', text: '已作废' },
  UNKNOWN: { color: 'default', text: '未知' },
};

const RevenueInvoicePreviewPage: React.FC = () => {
  const { billId = '' } = useParams<{ billId: string }>();
  const navigate = useNavigate();

  const canOperate = hasResource([ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]);

  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [reissueOpen, setReissueOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const { data, loading, refetch } = useQuery(GET_REVENUE_INVOICE_BY_BILL_ID, {
    variables: { billId },
    fetchPolicy: 'cache-and-network',
    skip: !billId,
  });

  const invoice: RevenueInvoiceType | null = data?.getRevenueInvoiceByBillId ?? null;
  const status = invoice?.status ?? 'UNKNOWN';
  const isSuccess = status === 'SUCCESS';
  const hasPdf = !!invoice?.pdfPreviewUrl;
  const hasDownload = !!invoice?.pdfDownloadUrl;

  // 预览：settlement 侧提供 inline 预签名 URL（Content-Disposition: inline + application/pdf），
  // iframe 导航直连即可内嵌渲染；不再 fetch→blob，规避跨源 fetch 的 CORS 依赖。下载走 attachment URL。
  const handleDownload = () => {
    if (invoice?.pdfDownloadUrl) openBillExportFile(invoice.pdfDownloadUrl);
  };

  const afterMutation = () => {
    setDueDateOpen(false);
    setReissueOpen(false);
    setSendOpen(false);
    refetch();
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/settlement/bill-detail/${billId}`)}>
            返回账单
          </Button>
          {invoice && (
            <>
              <span className="font-medium">{invoice.invoiceNo || `Invoice #${invoice.invoiceId}`}</span>
              <Tag color={STATUS_TAG[status]?.color}>{STATUS_TAG[status]?.text}</Tag>
              {invoice.dueDate != null && (
                <span className="text-gray-500 text-sm">Due Date：{formatYyyymmdd(invoice.dueDate)}</span>
              )}
              {invoice.invoiceAmount != null && (
                <span className="text-gray-500 text-sm">
                  金额：{formatCentsAmount(invoice.invoiceAmount, invoice.currency)}
                </span>
              )}
            </>
          )}
        </Space>
        <Space>
          <Button icon={<DownloadOutlined />} disabled={!isSuccess || !hasDownload} onClick={handleDownload}>
            下载 PDF
          </Button>
          {status !== 'CANCELLED' && (
            <Button
              icon={<CalendarOutlined />}
              disabled={!isSuccess || !canOperate}
              onClick={() => setDueDateOpen(true)}
            >
              修改 Due Date
            </Button>
          )}
          <Button
            icon={<RedoOutlined />}
            disabled={!canOperate}
            onClick={() => setReissueOpen(true)}
          >
            重开
          </Button>
          {status !== 'CANCELLED' && (
            <Button
              type="primary"
              icon={<SendOutlined />}
              disabled={!isSuccess || !canOperate}
              onClick={() => setSendOpen(true)}
            >
              推送
            </Button>
          )}
        </Space>
      </div>

      <div
        className="border rounded bg-gray-50"
        style={{ height: 'calc(100vh - 160px)', minHeight: 480 }}
      >
        {loading && !invoice ? (
          <div className="h-full flex items-center justify-center">
            <Spin tip="加载中..." />
          </div>
        ) : !invoice ? (
          <div className="h-full flex items-center justify-center">
            <Empty description="该账单暂未开具 Invoice" />
          </div>
        ) : hasPdf ? (
          <iframe
            title="invoice-pdf"
            src={invoice.pdfPreviewUrl ?? undefined}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        ) : (
          <div className="h-full flex flex-col items-center justify-center gap-3">
            <Spin />
            <div className="text-gray-500">
              {status === 'FAILED' ? 'PDF 生成失败，可稍后刷新重试' : 'PDF 生成中，请稍后刷新'}
            </div>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              刷新
            </Button>
          </div>
        )}
      </div>

      {invoice && (
        <>
          <InvoiceDueDateModal
            open={dueDateOpen}
            invoiceId={invoice.invoiceId}
            currentDueDate={invoice.dueDate}
            onCancel={() => setDueDateOpen(false)}
            onSuccess={afterMutation}
          />
          <InvoiceSendModal
            open={sendOpen}
            invoiceId={invoice.invoiceId}
            billId={billId}
            invoiceNo={invoice.invoiceNo}
            onCancel={() => setSendOpen(false)}
            onSuccess={afterMutation}
          />
        </>
      )}
      <InvoiceReissueModal
        open={reissueOpen}
        billId={billId}
        onCancel={() => setReissueOpen(false)}
        onSuccess={afterMutation}
      />
    </div>
  );
};

export default RevenueInvoicePreviewPage;
